import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, ExternalLink, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useXP } from '@/hooks/useXP';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { celebratePoints } from '@/utils/confetti';

interface SponsoredTipCardProps {
  ad: {
    id: string;
    company_name: string;
    offer_text: string;
    cta_url?: string;
  };
  onDismiss?: () => void;
}

export const SponsoredTipCard = ({ ad, onDismiss }: SponsoredTipCardProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { addXP } = useXP();
  const { toast } = useToast();
  const [dismissed, setDismissed] = useState(false);

  const handleClick = async () => {
    try {
      // Award +3 XP
      await addXP(3);
      celebratePoints(3);

      // Track view and click via rate-limited edge function
      await supabase.functions.invoke('submit-ad-view', {
        body: {
          ad_id: ad.id,
          placement: 'sponsored_tip',
          clicked: true,
        },
      });

      // Show toast
      toast({
        title: "🎉 " + (t('earnedXP') || '+3 XP earned!'),
        description: t('thanksForExploring') || "Thanks for checking this out!",
      });

      // Open URL
      if (ad.cta_url) {
        window.open(ad.cta_url, '_blank');
      }
    } catch (error) {
      console.error('Error tracking ad click:', error);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(`ad_dismissed_${ad.id}`, Date.now().toString());
    onDismiss?.();
  };

  if (dismissed) return null;

  return (
    <Card className="border-2 border-primary/30 bg-gradient-to-r from-primary/5 to-secondary/5 hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="mt-1">
            <Lightbulb className="w-5 h-5 text-primary" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <Badge variant="secondary" className="text-xs">
                💡 {t('sponsored') || 'Sponsored'} {t('tip') || 'Tip'}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="h-6 w-6 p-0 hover:bg-destructive/10"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
            
            <p className="text-sm font-medium mb-3 text-foreground">
              {ad.offer_text}
            </p>

            <div className="flex items-center justify-between">
              <Button
                onClick={handleClick}
                size="sm"
                variant="default"
                className="text-xs"
              >
                {t('learnMore') || 'Learn More'} +3 XP
                <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
              
              <span className="text-xs text-muted-foreground">
                {t('by') || 'by'} {ad.company_name}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
